// Reusable change-password form (used by the forced first-login screen and the
// in-app Account page). Asks for current + new + confirm.
import { useState } from "react";
import { KeyRound, Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const ACCENT = "#C75A1B";

export default function ChangePasswordForm({ onDone, currentLabel = "Current password" }) {
    const { changePassword } = useAuth();
    const [cur, setCur] = useState("");
    const [nw, setNw] = useState("");
    const [cf, setCf] = useState("");
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");

    async function submit(e) {
        e.preventDefault();
        setErr("");
        if (nw.length < 8) { setErr("New password must be at least 8 characters."); return; }
        if (nw !== cf) { setErr("New passwords don't match."); return; }
        setBusy(true);
        const res = await changePassword(cur, nw);
        setBusy(false);
        if (res.ok) { toast.success("Password updated"); onDone?.(); }
        else setErr(res.error || "Could not change password");
    }

    const field = "w-full bg-white border rounded-[10px] px-3.5 py-2.5 text-[13px] text-[#1F1F1F] outline-none focus:border-[#C75A1B] focus:ring-2 focus:ring-[#FBEFE5]";
    const lbl = "block text-[11px] font-semibold uppercase tracking-wide mb-1.5";

    return (
        <form onSubmit={submit} className="space-y-3.5" data-testid="change-password-form">
            <div>
                <span className={lbl} style={{ color: "#6F6A64" }}>{currentLabel}</span>
                <input data-testid="cp-current" type={show ? "text" : "password"} value={cur} onChange={(e) => setCur(e.target.value)} required autoComplete="current-password" className={field} />
            </div>
            <div>
                <span className={lbl} style={{ color: "#6F6A64" }}>New password</span>
                <div className="relative">
                    <input data-testid="cp-new" type={show ? "text" : "password"} value={nw} onChange={(e) => setNw(e.target.value)} required autoComplete="new-password" className={field} />
                    <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2" tabIndex={-1}>
                        {show ? <EyeOff className="w-4 h-4 text-[#9A968F]" /> : <Eye className="w-4 h-4 text-[#9A968F]" />}
                    </button>
                </div>
            </div>
            <div>
                <span className={lbl} style={{ color: "#6F6A64" }}>Confirm new password</span>
                <input data-testid="cp-confirm" type={show ? "text" : "password"} value={cf} onChange={(e) => setCf(e.target.value)} required autoComplete="new-password" className={field} />
            </div>
            {err && <div data-testid="cp-error" className="px-3.5 py-2.5 rounded-[10px] text-[12.5px]" style={{ background: "#FBE8E5", color: "#B33A2E", border: "1px solid #F2D0CA" }}>{err}</div>}
            <button type="submit" data-testid="cp-submit" disabled={busy}
                className="w-full py-3 rounded-[10px] text-[13px] font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: ACCENT }}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />} Update password
            </button>
        </form>
    );
}
